import { Controller } from "@hotwired/stimulus"

// The custom function and MCP tool testers: sends the parameters plus the page's unsaved config fields (named
// `${fieldPrefix}[field]`) to the tester endpoint and shows the response, with the server's warning when the AI agent
// would only see the start of it (ToolResponse::MAX_CHARS).
export default class extends Controller {
  static targets = [
    "parametersJson", "result", "rawBody", "processedBody",
    "processedBodySection", "submitButton", "authenticatedEmail",
    "authenticatedPhone", "errorMessage", "requestUrl", "requestBody",
    "requestBodySection", "requestHeaders", "requestHeadersSection", "requestDetails", "loading", "placeholder",
    "truncationWarning", "toolSelect"
  ]
  static values = { url: String, fieldPrefix: { type: String, default: "custom_bot_function" } }

  // The MCP server tester's tool picker: point the tester at the chosen tool and reset its parameters.
  selectTool() {
    const option = this.toolSelectTarget.selectedOptions[0]
    this.urlValue = option.dataset.url
    this.fieldPrefixValue = option.dataset.fieldPrefix
    const editor = this.parametersJsonTarget.aceEditor
    if (editor) editor.setValue(option.dataset.skeleton, -1)
    else this.parametersJsonTarget.value = option.dataset.skeleton
    if (this.hasResultTarget) this.resultTarget.classList.add('hidden')
    if (this.hasErrorMessageTarget) this.errorMessageTarget.classList.add('hidden')
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove('hidden')
  }

  async submit(event) {
    event.preventDefault()

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = 'Sending...'
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add('hidden')
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove('hidden')
    if (this.hasResultTarget) this.resultTarget.classList.add('hidden')
    if (this.hasErrorMessageTarget) this.errorMessageTarget.classList.add('hidden')

    let parameters
    try {
      parameters = this.parametersJsonTarget.value.trim() || '{}'
      JSON.parse(parameters)
    } catch {
      this.displayError('Invalid JSON in parameters')
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = 'Send Request'
      if (this.hasLoadingTarget) this.loadingTarget.classList.add('hidden')
      return
    }

    const csrfToken = document.getElementsByName("csrf-token")[0].content
    const body = new FormData()
    body.append('parameters', parameters)
    if (this.hasAuthenticatedEmailTarget && this.authenticatedEmailTarget.value) {
      body.append('authenticated_email', this.authenticatedEmailTarget.value)
    }
    if (this.hasAuthenticatedPhoneTarget && this.authenticatedPhoneTarget.value) {
      body.append('authenticated_phone', this.authenticatedPhoneTarget.value)
    }

    const configFields = [
      'method', 'path', 'format', 'query_params_json', 'request_headers_json',
      'request_body_template', 'response_body_template'
    ]
    configFields.forEach(field => {
      const el = document.querySelector(`[name="${this.fieldPrefixValue}[${field}]"]`)
      if (el) body.append(field, el.value)
    })

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
        body: body
      })
      const data = await response.json()
      this.displayResult(data)
    } catch (error) {
      this.displayError(error.message)
    } finally {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = 'Send Request'
      if (this.hasLoadingTarget) this.loadingTarget.classList.add('hidden')
    }
  }

  displayResult(data) {
    if (data.error) {
      this.displayError(data.error)
      return
    }

    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add('hidden')
    this.resultTarget.classList.remove('hidden')

    const isSuccess = data.status >= 200 && data.status < 300
    const pillEl = this.resultTarget.querySelector('.pill-sm')
    if (pillEl) {
      pillEl.textContent = `${data.status}`
      pillEl.className = `pill-sm pill:${isSuccess ? 'green' : 'red'}`
    }

    if (this.hasRequestDetailsTarget) {
      this.requestDetailsTarget.classList.remove('hidden')
    }
    if (this.hasRequestUrlTarget) {
      this.requestUrlTarget.textContent = data.request_url || ''
    }
    if (this.hasRequestHeadersSectionTarget) {
      const headers = data.request_headers || {}
      this.requestHeadersSectionTarget.classList.toggle('hidden', Object.keys(headers).length === 0)
      this.requestHeadersTarget.textContent = JSON.stringify(headers, null, 2)
    }
    if (this.hasRequestBodySectionTarget) {
      if (data.request_body) {
        this.requestBodySectionTarget.classList.remove('hidden')
        this.requestBodyTarget.textContent = this.formatJson(data.request_body)
      } else {
        this.requestBodySectionTarget.classList.add('hidden')
      }
    }

    this.rawBodyTarget.textContent = this.formatJson(data.raw_body) || 'Empty response'
    this.displayTruncationWarning(data)

    if (this.hasProcessedBodySectionTarget) {
      if (data.processed_body) {
        this.processedBodySectionTarget.classList.remove('hidden')
        this.processedBodyTarget.textContent = this.formatJson(data.processed_body)
      } else {
        this.processedBodySectionTarget.classList.add('hidden')
      }
    }
  }

  displayTruncationWarning(data) {
    if (!this.hasTruncationWarningTarget) return

    this.truncationWarningTarget.classList.toggle('hidden', !data.agent_response_warning)
    this.truncationWarningTarget.textContent = data.agent_response_warning || ''
  }

  displayError(message) {
    if (this.hasResultTarget) this.resultTarget.classList.add('hidden')
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add('hidden')
    this.errorMessageTarget.classList.remove('hidden')
    this.errorMessageTarget.textContent = message
  }

  formatJson(str) {
    if (!str) return str
    try {
      return JSON.stringify(JSON.parse(str), null, 2)
    } catch {
      return str
    }
  }
}
